import React, { useMemo, useRef } from "react";
// import PropTypes from "prop-types";
import { API_PARAM } from "../../constants/apiParams";
import { useTranslation } from "react-i18next";
import { toastActions } from "@base-provider";
import {
  DateFormatter,
  CELL_RENDERER,
  SFDatagrid,
  SFPageContainer,
  SFActionBar,
  ACTION_BAR_ACTION_NAMES,
  ACTION_BAR_ACTION_TYPES,
  SFAddIcon,
  sfDatagridHelpers,
} from "@sf-core";
import { Logger } from "@base-provider";
import { useSelector, useDispatch } from "react-redux";
import { GridNames } from "../../constants/gridConfig";
import { Claims } from "../../constants/claims";
import { useNavigate } from "react-router-dom";
import { ExportFileNames } from "../../constants/exportFileNames";
import { exportUsersGrid } from "../../services/userService";
import { PATHS } from "../../constants/paths";

const Users = () => {
  const gridRef = useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const usersGridLayout = useSelector((state) =>
    state.gridLayout.find((grid) => grid.gridName === GridNames.USERS_GRID)
  );

  const rowHandler = (rowData = null) => {
    Logger.of("App.usersGrid.rowHandler").info({ rowData });
    navigate(PATHS.ADMIN_USERS_USER_PROFILE.replace(":id", rowData.id));
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: t("usersScreen.usersGrid.userIdColumn"),
        field: "id",
        hide: true,
        filter: false,
      },
      {
        headerName: t("usersScreen.usersGrid.userNameColumn"),
        field: "name",
        avatar: true,
        minWidth: 180,
        filter: true,
        cellRenderer: CELL_RENDERER.EDIT_CLICKABLE,
        cellRendererParams: {
          onClick: rowHandler,
        },
        filterParams: {
          order: 2,
          advancedQuery: true,
        },
      },
      {
        headerName: t("usersScreen.usersGrid.userUserNameColumn"),
        field: "userName",
        minWidth: 180,
        filter: true,
        filterParams: {
          order: 3,
          advancedQuery: true,
        },
      },
      {
        headerName: t("usersScreen.usersGrid.userEmailColumn"),
        field: "email",
        minWidth: 180,
        filter: true,
        filterParams: {
          order: 4,
          advancedQuery: true,
        },
      },
      {
        headerName: t("usersScreen.usersGrid.userRoleColumn"),
        field: "userRole",
        minWidth: 150,
        filter: true,
        filterParams: {
          order: 5,
          advancedQuery: true,
        },
      },
      {
        headerName: t("usersScreen.usersGrid.userCreatedAtColumn"),
        field: "createdAt",
        filter: false,
        valueFormatter: DateFormatter,
        minWidth: 180,
        // filterParams: {
        //   valueFormatter: DateFormatter,
        // },
      },
      {
        headerName: t("usersScreen.usersGrid.userCreatedByColumn"),
        field: "createdBy",
        minWidth: 180,
      },
      {
        headerName: t("usersScreen.usersGrid.userUpdatedAtColumn"),
        field: "updatedAt",
        filter: false,
        valueFormatter: DateFormatter,
        minWidth: 180,
        // filterParams: {
        //   valueFormatter: DateFormatter,
        // },
      },
      {
        headerName: t("usersScreen.usersGrid.userUpdatedByColumn"),
        field: "updatedBy",
        minWidth: 180,
      },
      {
        headerName: t("usersScreen.usersGrid.userActiveColumn"),
        field: "isActive",
        cellRenderer: CELL_RENDERER.BOOLEAN,
        headerClass: "header-text-center",
        minWidth: 120,
        filter: "boolean",
        filterParams: {
          order: 1,
          dataType: "boolean",
        },
      },
      {
        headerName: t("usersScreen.usersGrid.userDeletedAtColumn"),
        field: "deletedAt",
        filter: false,
        minWidth: 180,
        valueFormatter: DateFormatter,
        // filterParams: {
        //   valueFormatter: DateFormatter,
        // },
      },
      {
        headerName: t("usersScreen.usersGrid.userDeletedByColumn"),
        field: "deletedBy",
        minWidth: 180,
      },
    ],
    []
  );

  const handlerNewUsersFramework = () => {
    navigate(PATHS.ADMIN_USERS_ADD_USER);
  };

  const handleUsersDownload = () => {
    exportUsersGrid(usersGridLayout?.filterModel ?? {});
  };

  const messages = {
    addSuccess: t("usersScreen.messages.addSuccess"),
    editSuccess: t("usersScreen.messages.editSuccess"),
    deleteSuccess: t("usersScreen.messages.deleteSuccess"),
    restoreSuccess: t("usersScreen.messages.restoreSuccess"),
  };

  const actionDefs = [
    {
      name: ACTION_BAR_ACTION_NAMES.DOWNLOAD,
      type: ACTION_BAR_ACTION_TYPES.ICON,
      children: [
        {
          name: t("actionBar.downloadUsers"),
          label: t("usersScreen.usersGridTitle"),
          onClick: () => {
            dispatch(
              toastActions.showInfoMessage(
                t("usersScreen.usersForm.beginDownload")
              )
            );
            handleUsersDownload();
          },
        },
      ],
    },
    {
      name: ACTION_BAR_ACTION_NAMES.FILTER,
      type: ACTION_BAR_ACTION_TYPES.ICON,
      filterParams: { gridName: GridNames.USERS_GRID, advancedQuery: true },
    },
    {
      name: ACTION_BAR_ACTION_NAMES.ADD,
      type: ACTION_BAR_ACTION_TYPES.BUTTON,
      onClick: handlerNewUsersFramework,
      permissions: [Claims.AdminUsersAdd],
      buttonProps: {
        label: t("adminRoutes.newUser"),
        variant: "primary",
        icon: <SFAddIcon />,
        iconPlacement: "start",
      },
    },
  ];

  return (
    <>
      <SFPageContainer disableGutters>
        <SFActionBar
          actionDefs={actionDefs}
          titleProps={{
            title: t("adminRoutes.users"),
          }}
          search
          searchProps={{
            onInputChange: (value) => {
              sfDatagridHelpers.updateSearchTextFilter(gridRef, value);
            },
            gridName: GridNames.USERS_GRID,
          }}
        />
        <SFDatagrid
          ref={gridRef}
          permissions={{
            add: Claims.AdminUsersAdd,
            edit: Claims.AdminUsersEdit,
            delete: Claims.AdminUsersDelete,
            export: Claims.AdminUsersExport,
          }}
          persistState
          exportFileName={ExportFileNames.USERS}
          basicFiltering
          messages={messages}
          canDownload={false}
          columnDefs={columnDefs}
          gridName={GridNames.USERS_GRID}
          datasourceUrl={API_PARAM.USERS_GRID}
          filterValuesUrl={API_PARAM.USERS_COL_FILTER}
        />
      </SFPageContainer>
    </>
  );
};

export default Users;
